import api from '../utils/api'

/**
 * Private recipient documents are not stored as URLs. A case manager gets a
 * short-lived signed link from casemanager-be (GET /case-documents/:caseId/:receiptId),
 * which asks scale-be to re-check the case manager's assignment and the
 * receipt's membership on every request. The link is valid for 60 seconds, so
 * callers must use it straight away and never keep it.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const CASE_ID = /^[1-9][0-9]*$/

export const isReceiptId = (v) => typeof v === 'string' && UUID.test(v)

// A Scale case id. CAR cases awaiting scheduling carry a UUID here instead.
export const isCaseId = (v) =>
    (typeof v === 'string' || typeof v === 'number') &&
    CASE_ID.test(String(v)) &&
    Number.isSafeInteger(Number(v))

// One fixed error for every failure, so nothing from the response reaches the UI.
const unavailable = () => new Error('Document unavailable')

export async function resolveStaffDocument({ caseId, receiptId, signal }) {
    if (!isCaseId(caseId) || !isReceiptId(receiptId)) throw unavailable()
    let res
    try {
        res = await api.get(`/case-documents/${String(caseId)}/${receiptId.toLowerCase()}`, { signal })
    } catch {
        throw unavailable()
    }
    const d = res?.data
    if (res?.status !== 200 || !d || typeof d.url !== 'string' || typeof d.expires_at !== 'string' || typeof d.fileName !== 'string') {
        throw unavailable()
    }
    let url
    try {
        url = new URL(d.url)
    } catch {
        throw unavailable()
    }
    if (url.protocol !== 'https:' || url.username || url.password) throw unavailable()
    // The server sets the expiry from its own clock; the browser clock may be
    // skewed, so it is not compared here. The link is used immediately instead.
    if (!Number.isFinite(Date.parse(d.expires_at))) throw unavailable()
    return { url: url.href, fileName: d.fileName }
}
